import { Link } from '@inertiajs/react'
import { Activity } from 'lucide-react'
import { PanelEmptyState } from '@/frontend/components/EmptyState'

interface ActivityLog {
  id: string
  event_name: string
  description: string
  subject_type: string | null
  subject_id: string | null
  actor_type: string | null
  actor_id: string | null
  metadata: Record<string, unknown>
  occurred_at: string
}

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Props {
  page_title: string
  activity_logs: ActivityLog[]
  pagination: Pagination
}

function formatDate(isoString: string): string {
  const date = new Date(isoString)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function eventColor(eventName: string): string {
  if (eventName.includes('synced')) return 'bg-blue-500'
  if (eventName.includes('calculated')) return 'bg-green-500'
  if (eventName.includes('imported')) return 'bg-purple-500'
  return 'bg-muted0'
}

export default function ActivityLogsIndex({ page_title, activity_logs, pagination }: Props) {
  return (
    <>
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{page_title}</h1>
          <p className="text-sm text-muted-foreground mt-1">Acompanhe em tempo real o que seus agentes estão executando.</p>
        </div>

        {activity_logs.length === 0 ? (
          <PanelEmptyState
            icon={Activity}
            framed={false}
            title="Nenhuma atividade registrada ainda"
            description="O que os agentes executarem aparece aqui em tempo real."
          />
        ) : (
          <div className="space-y-1">
            {activity_logs.map((log) => (
              <div key={log.id} className="flex items-start gap-4 py-3 px-4 hover:bg-accent rounded-lg transition-colors">
                <div className="flex-shrink-0 mt-1.5">
                  <div className={`w-2.5 h-2.5 rounded-full ${eventColor(log.event_name)}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground">{log.description}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{formatDate(log.occurred_at)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {pagination.total_pages > 1 && (
          <nav className="flex items-center justify-between mt-8 pt-4 border-t border-border">
            <div className="text-sm text-muted-foreground">
              Página {pagination.current_page} de {pagination.total_pages} ({pagination.total_count} atividades)
            </div>
            <div className="flex gap-2">
              {pagination.current_page > 1 && (
                <Link
                  href={`/dashboard/activity_logs?page=${pagination.current_page - 1}`}
                  className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent transition-colors"
                >
                  Anterior
                </Link>
              )}
              {pagination.current_page < pagination.total_pages && (
                <Link
                  href={`/dashboard/activity_logs?page=${pagination.current_page + 1}`}
                  className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent transition-colors"
                >
                  Próxima
                </Link>
              )}
            </div>
          </nav>
        )}
      </div>
    </>
  )
}
